import {
  Accordion,
  Badge,
  Box,
  Button,
  Group,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SYSTEM_STAGES, type SystemStage } from "@msc/shared";

import { ApiError } from "../../lib/api.js";
import { formatIsoDate, fromLocalDateString, toLocalDateString } from "../../lib/date.js";
import { DATE_POPOVER_DOWNWARDS } from "../../theme.js";
import { hasRole, useCurrentUser } from "../../hooks/useAuth.js";
import { useCustomer } from "../../hooks/useCustomers.js";
import {
  useCreateSystem,
  useCustomerSystems,
  useUpdateSystem,
  useUpsertSoftware,
  type CustomerSystem,
  type SoftwareEntry,
} from "../../hooks/useMaintenance.js";
import { LifecycleBadge } from "./LifecycleBadge.js";
import {
  MaintenanceEndModal,
  type MaintenanceEndTarget,
} from "./MaintenanceEndModal.js";
import { SoftwarePicker } from "./SoftwarePicker.js";
import { SystemAttributesSection } from "./SystemAttributesSection.js";
import { SystemMaintenanceEvents } from "./SystemMaintenanceEvents.js";

interface EngagementOption {
  value: string;
  label: string;
}

/**
 * Reiter „Systeme" der Kundenakte: Systembestand samt Softwareständen und
 * Wartungs-Ampel. Beauftragungen holt die Karte selbst über den
 * Customer-360-Endpunkt — dieselbe Query, die auch die Beauftragungs-Karte
 * speist, react-query dedupliziert sie.
 *
 * `focusSystemId` kommt aus dem Suchparameter `systemId` der Kundenakte
 * (Befund U8/U9): der Deep-Link von der Wartungsseite soll nicht nur den
 * richtigen Reiter treffen, sondern auch das gemeinte System aufklappen und
 * in den sichtbaren Bereich holen.
 */
export function CustomerSystemsCard({
  customerId,
  focusSystemId,
}: {
  customerId: string;
  focusSystemId?: string | undefined;
}) {
  const { t } = useTranslation();
  const { data: systems, isLoading } = useCustomerSystems(customerId);
  const { data: customer } = useCustomer(customerId);
  const { user } = useCurrentUser();

  /**
   * Wartungsdaten dieses Kunden pflegen — dieselbe Regel wie im Server
   * (`maintenance/authz.ts`): Geschäftsleitung und Admin überall,
   * Bereichsleitung und Stellvertretung in ihren Segmenten, der Service Lead
   * für die Kunden seiner Beauftragungen.
   *
   * Bis zum 2026-08-20 durfte hier jeder Angemeldete schreiben (Spec §E6,
   * zurückgenommen). Ohne diese Prüfung stünden die Knöpfe weiter für jeden da
   * und lieferten seither einen 403 — genau die Sorte Feld, die etwas
   * verspricht, was sie nicht halten kann.
   */
  const darfPflegen =
    hasRole(user, ["admin", "geschaeftsleitung"]) ||
    (customer?.segments ?? []).some((seg) =>
      (user?.roles ?? []).some(
        (r: { role: string; segmentId: string | null }) =>
          (r.role === "bereichsleiter" || r.role === "stellvertreter") &&
          r.segmentId === seg.id,
      ),
    ) ||
    (customer?.engagements ?? []).some(
      (e) => e.leadEmployeeId !== null && e.leadEmployeeId === user?.employeeId,
    );
  const [creating, setCreating] = useState(false);
  // Aufgeklappte Systeme bleiben Nutzerzustand (mehrere gleichzeitig möglich);
  // der Deep-Link ergänzt nur einen Eintrag, statt die Auswahl zu übernehmen.
  const [openSystemIds, setOpenSystemIds] = useState<string[]>(
    focusSystemId ? [focusSystemId] : [],
  );

  useEffect(() => {
    if (!focusSystemId) return;
    setOpenSystemIds((prev) =>
      prev.includes(focusSystemId) ? prev : [...prev, focusSystemId],
    );
  }, [focusSystemId]);

  // Erst wenn die Systemliste da ist, existiert das Ziel im DOM — deshalb
  // hängt der Effekt zusätzlich an `systems`.
  useEffect(() => {
    if (!focusSystemId || !systems?.some((s) => s.id === focusSystemId)) return;
    const target = document.querySelector(`[data-system-id="${focusSystemId}"]`);
    target?.scrollIntoView({ block: "center" });
  }, [focusSystemId, systems]);

  const engagementOptions: EngagementOption[] = (customer?.engagements ?? []).map((e) => {
    const segment = customer?.segments.find((s) => s.id === e.segmentId);
    return { value: e.id, label: segment ? `${e.name} (${segment.name})` : e.name };
  });

  // Befund U12: Der Reiter zeigte hier nur ein kleines „Lädt…" und wirkte
  // dabei leer. Skeletons in der Form der kommenden Systemzeilen sagen
  // dasselbe, sehen aber nach Arbeit statt nach Defekt aus (Muster wie
  // wartung.tsx und SoftwareCatalogTab).
  if (isLoading) {
    return (
      <Stack gap="sm">
        <Skeleton height={56} />
        <Skeleton height={56} />
        <Skeleton height={56} />
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {darfPflegen && (
        <Group justify="flex-end">
          <Button size="xs" variant="light" onClick={() => setCreating((v) => !v)}>
            {t("maintenance.card.addSystem")}
          </Button>
        </Group>
      )}

      {creating && (
        <NewSystemForm
          customerId={customerId}
          engagementOptions={engagementOptions}
          onDone={() => setCreating(false)}
        />
      )}

      {(systems ?? []).length === 0 ? (
        <Text c="dimmed">{t("maintenance.card.empty")}</Text>
      ) : (
        <Accordion
          variant="separated"
          multiple
          value={openSystemIds}
          onChange={setOpenSystemIds}
        >
          {(systems ?? []).map((system) => (
            <SystemAccordionItem
              key={system.id}
              customerId={customerId}
              system={system}
              engagementOptions={engagementOptions}
              darfPflegen={darfPflegen}
            />
          ))}
        </Accordion>
      )}
    </Stack>
  );
}

function NewSystemForm({
  customerId,
  engagementOptions,
  onDone,
}: {
  customerId: string;
  engagementOptions: EngagementOption[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const createSystem = useCreateSystem(customerId);
  const [name, setName] = useState("");
  const [systemKey, setSystemKey] = useState("");
  const [stage, setStage] = useState<SystemStage>("produktiv");
  const [engagementId, setEngagementId] = useState<string | null>(null);
  const [proactive, setProactive] = useState(false);

  const canSubmit = name.trim().length > 0;

  return (
    <Box p="sm" style={{ border: "1px solid var(--mantine-color-gray-3)", borderRadius: 8 }}>
      <Stack gap="xs">
        <Group gap="xs" wrap="wrap" align="flex-end">
          <TextInput
            size="xs"
            label={t("maintenance.card.systemName")}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            style={{ minWidth: 200 }}
          />
          <TextInput
            size="xs"
            label={t("maintenance.card.systemKey")}
            value={systemKey}
            onChange={(e) => setSystemKey(e.currentTarget.value)}
            style={{ width: 140 }}
          />
          <Select
            size="xs"
            label={t("maintenance.card.stage")}
            data={SYSTEM_STAGES.map((s) => ({ value: s, label: t(`maintenance.stage.${s}`) }))}
            value={stage}
            allowDeselect={false}
            onChange={(v) => v && setStage(v as SystemStage)}
            style={{ width: 160 }}
          />
          <Select
            size="xs"
            label={t("maintenance.card.engagement")}
            placeholder={t("maintenance.card.noEngagement")}
            data={engagementOptions}
            value={engagementId}
            clearable
            onChange={(v) => {
              setEngagementId(v);
              if (!v) setProactive(false);
            }}
            style={{ minWidth: 220 }}
          />
        </Group>
        <Stack gap={2}>
          <Switch
            label={t("maintenance.card.proactive")}
            checked={proactive}
            disabled={engagementId === null}
            onChange={(e) => setProactive(e.currentTarget.checked)}
          />
          {/* Befund U1: ein Tooltip auf einem deaktivierten Switch feuert in
              den meisten Browsern nicht (disabled Elemente lösen keine
              Hover-Events aus) — sichtbarer Hinweistext statt Hover-Erklärung. */}
          {engagementId === null && (
            <Text size="xs" c="dimmed">
              {t("maintenance.card.proactiveNeedsEngagement")}
            </Text>
          )}
        </Stack>
        <Group gap="xs">
          <Button
            size="xs"
            disabled={!canSubmit}
            loading={createSystem.isPending}
            onClick={() =>
              createSystem.mutate(
                {
                  name: name.trim(),
                  systemKey: systemKey.trim() || undefined,
                  stage,
                  engagementId: engagementId ?? undefined,
                  proactiveMaintenance: proactive,
                },
                { onSuccess: onDone },
              )
            }
          >
            {t("common.save")}
          </Button>
          <Button size="xs" variant="subtle" onClick={onDone}>
            {t("common.cancel")}
          </Button>
        </Group>
        {createSystem.isError && (
          <Text c="red" size="xs">
            {t("maintenance.card.saveError")}
          </Text>
        )}
      </Stack>
    </Box>
  );
}

function SystemAccordionItem({
  customerId,
  system,
  engagementOptions,
  darfPflegen,
}: {
  customerId: string;
  system: CustomerSystem;
  engagementOptions: EngagementOption[];
  /** Wartungsdaten dieses Kunden pflegen — s. CustomerSystemsCard. */
  darfPflegen: boolean;
}) {
  const { t } = useTranslation();
  const updateSystem = useUpdateSystem(customerId);
  const [addingSoftware, setAddingSoftware] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  // Der Softwarestand trägt bereits alles, was die Maske als Einstieg braucht
  // (Produkt, Version) — sie holt sich Linie und Reichweite selbst.
  const [maintenanceEndTarget, setMaintenanceEndTarget] = useState<MaintenanceEndTarget | null>(
    null,
  );

  return (
    <Accordion.Item value={system.id} data-system-id={system.id}>
      <Group wrap="nowrap" gap={4} pr="md">
        <Accordion.Control>
          <Group gap="xs" wrap="wrap">
            <Text fw={600}>{system.name}</Text>
            {system.systemKey && (
              <Badge variant="light" color="gray" size="sm">
                {system.systemKey}
              </Badge>
            )}
            <Badge variant="light" size="sm">
              {t(`maintenance.stage.${system.stage}`)}
            </Badge>
            <Text size="sm" c="dimmed">
              {system.engagementName ?? t("maintenance.card.noEngagement")}
            </Text>
          </Group>
        </Accordion.Control>
        <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
          {/* Der Trigger `cs_disable_proactive_on_engagement_loss` schaltet
              „proaktiv warten" ab, sobald die Beauftragung wegfällt — und
              schaltet es bewusst NICHT von allein wieder ein, wenn die
              Zuständigkeit zurückkehrt. Ohne diesen Merker wäre ein System,
              dem der Schalter abhandengekommen ist, von einem nie
              eingeschalteten nicht zu unterscheiden. */}
          {system.proactiveAutoDisabledAt !== null && (
            <Tooltip label={t("maintenance.card.proactiveAutoDisabledHint")} multiline w={280}>
              <Badge color="orange" variant="light" size="sm">
                {t("maintenance.card.proactiveAutoDisabled")}
              </Badge>
            </Tooltip>
          )}
          <Tooltip
            label={t("maintenance.card.proactiveNeedsEngagement")}
            disabled={system.engagementId !== null}
          >
            {/* Befund U1: Tooltip direkt am deaktivierten Switch feuert nicht
                (disabled Formularelemente lösen keine Hover-Events aus) —
                die Hover-Erkennung braucht einen nicht-deaktivierten
                Wrapper, wie von Mantine für Tooltips auf deaktivierten
                Elementen empfohlen. */}
            <Box style={{ display: "inline-block" }}>
              <Switch
                size="sm"
                label={t("maintenance.card.proactive")}
                checked={system.proactiveMaintenance}
                disabled={system.engagementId === null || updateSystem.isPending}
                onChange={(e) =>
                  updateSystem.mutate({
                    id: system.id,
                    proactiveMaintenance: e.currentTarget.checked,
                  })
                }
              />
            </Box>
          </Tooltip>
        </Group>
      </Group>

      <Accordion.Panel>
        <Stack gap="sm">
          <Group gap="xs" wrap="wrap" align="flex-end">
            {/*
              Der Name war bis zum 2026-08-19 nur in der Anlage-Maske
              erreichbar — `PATCH /systems/:id` nimmt ihn laengst entgegen, die
              Karte bot bloss kein Feld an. Der Import benennt die Systeme nach
              einem festen Muster („<Kunde> - PV"), und genau die will man
              danach umbenennen.
            */}
            <SystemNameField
              system={system}
              onSave={(name) => updateSystem.mutate({ id: system.id, name })}
              pending={updateSystem.isPending}
            />
            <Select
              size="xs"
              label={t("maintenance.card.stage")}
              data={SYSTEM_STAGES.map((s) => ({ value: s, label: t(`maintenance.stage.${s}`) }))}
              value={system.stage}
              allowDeselect={false}
              onChange={(v) => v && updateSystem.mutate({ id: system.id, stage: v as SystemStage })}
              style={{ width: 160 }}
            />
            <Tooltip
              label={t("maintenance.card.proactiveNeedsEngagement")}
              disabled={!system.proactiveMaintenance}
            >
              <Select
                size="xs"
                label={t("maintenance.card.engagement")}
                placeholder={t("maintenance.card.noEngagement")}
                data={engagementOptions}
                value={system.engagementId}
                clearable={!system.proactiveMaintenance}
                onChange={(v) => updateSystem.mutate({ id: system.id, engagementId: v })}
                style={{ minWidth: 220 }}
              />
            </Tooltip>
          </Group>

          {updateSystem.isError && (
            <Text c="red" size="xs">
              {updateSystem.error instanceof ApiError
                ? updateSystem.error.message
                : t("maintenance.card.saveError")}
            </Text>
          )}

          {system.software.length === 0 ? (
            <Text c="dimmed" size="sm">
              {t("maintenance.card.noSoftware")}
            </Text>
          ) : (
            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("maintenance.card.tableProduct")}</Table.Th>
                  <Table.Th>{t("maintenance.card.tableVersion")}</Table.Th>
                  <Table.Th>{t("maintenance.card.tableAsOf")}</Table.Th>
                  <Table.Th>{t("maintenance.card.tableConfirmedBy")}</Table.Th>
                  <Table.Th>{t("maintenance.card.tableStatus")}</Table.Th>
                  <Table.Th>{t("common.actions")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {system.software.map((sw) => (
                  <Table.Tr key={sw.productId}>
                    <Table.Td>{sw.productName}</Table.Td>
                    <Table.Td>{sw.versionLabel}</Table.Td>
                    <Table.Td>{formatIsoDate(sw.asOf)}</Table.Td>
                    <Table.Td>{sw.confirmedByName ?? "–"}</Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <LifecycleBadge
                          status={sw.status}
                          stale={sw.stale}
                          expired={sw.expired}
                          maintenanceEnd={sw.maintenanceEnd}
                          granularity={sw.granularity}
                          inheritedFromLine={sw.inheritedFromLine}
                          thresholdsFromProduct={sw.thresholdsFromProduct}
                          warnOrangeMonths={sw.warnOrangeMonths}
                          warnRedMonths={sw.warnRedMonths}
                          staleAfterMonths={sw.staleAfterMonths}
                        />
                        {/* Weg aus der Ampel „unbekannt": Genau hier fällt auf,
                            dass das Wartungsende fehlt — und genau hier lässt
                            es sich jetzt nachtragen, statt über die
                            Einstellungen. */}
                        {sw.maintenanceEnd === null && darfPflegen && (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            onClick={() => setMaintenanceEndTarget(sw)}
                          >
                            {t("maintenance.card.maintenanceEnd.fillIn")}
                          </Button>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {darfPflegen && (
                      <Group gap={4} wrap="nowrap">
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          onClick={() =>
                            setEditingProductId(
                              editingProductId === sw.productId ? null : sw.productId,
                            )
                          }
                        >
                          {t("common.edit")}
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          onClick={() => setMaintenanceEndTarget(sw)}
                        >
                          {t("maintenance.card.maintenanceEnd.action")}
                        </Button>
                      </Group>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          <MaintenanceEndModal
            target={maintenanceEndTarget}
            onClose={() => setMaintenanceEndTarget(null)}
          />

          {editingProductId !== null && (
            <SoftwareForm
              customerId={customerId}
              systemId={system.id}
              initial={system.software.find((sw) => sw.productId === editingProductId)}
              onDone={() => setEditingProductId(null)}
            />
          )}

          <Group>
            <Button size="xs" variant="light" onClick={() => setAddingSoftware((v) => !v)}>
              {t("maintenance.card.addSoftware")}
            </Button>
          </Group>
          {addingSoftware && (
            <SoftwareForm
              customerId={customerId}
              systemId={system.id}
              onDone={() => setAddingSoftware(false)}
            />
          )}

          <SystemMaintenanceEvents customerId={customerId} systemId={system.id} />

          <SystemAttributesSection
            customerId={customerId}
            systemId={system.id}
            attributes={system.attributes}
          />
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function SoftwareForm({
  customerId,
  systemId,
  initial,
  onDone,
}: {
  customerId: string;
  systemId: string;
  /** Bereits vorhandener Softwarestand (Befund B2) — vorbelegt statt leeres Formular bei „Bearbeiten". */
  initial?: SoftwareEntry;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const upsertSoftware = useUpsertSoftware(customerId);
  const [picked, setPicked] = useState<{ productId: string; versionId: string } | null>(
    initial ? { productId: initial.productId, versionId: initial.versionId } : null,
  );
  const [asOf, setAsOf] = useState<Date | null>(
    initial ? fromLocalDateString(initial.asOf) : new Date(),
  );

  /**
   * Eine andere Version heißt: neu erhoben — also heute.
   *
   * Beim „Bearbeiten" stand bisher das gespeicherte Erhebungsdatum im Feld und
   * blieb dort. Wer die Version aktualisierte (1.71.67 → 1.71.81), speicherte
   * damit die Behauptung, man habe den neuen Stand schon am alten Datum
   * gesehen. Das ist nicht nur schief protokolliert, es hat Folgen: Die
   * Veraltet-Markierung rechnet auf `asOf`, und der Massen-Import lässt einen
   * erfassten Stand stehen, wenn dessen `asOf` nach dem Stichtag der Datei
   * liegt (gemeldet 2026-09-09).
   *
   * Deshalb springt das Datum auf heute, sobald eine ANDERE Version gewählt
   * wird — und nur dann. Wer bloß einen Tippfehler am selben Stand korrigiert,
   * behält das ursprüngliche Erhebungsdatum. Überschreiben lässt es sich
   * weiterhin von Hand; das Feld bleibt, was es war.
   */
  const waehleVersion = (gewaehlt: { productId: string; versionId: string }) => {
    setPicked(gewaehlt);
    if (initial && gewaehlt.versionId !== initial.versionId) {
      setAsOf(new Date());
    }
  };

  return (
    <Box p="sm" style={{ border: "1px dashed var(--mantine-color-gray-4)", borderRadius: 8 }}>
      <Stack gap="xs">
        <SoftwarePicker
          onPick={waehleVersion}
          pickLabel={t("maintenance.picker.selectVersion")}
          initial={
            initial
              ? {
                  productId: initial.productId,
                  productName: initial.productName,
                  versionId: initial.versionId,
                  versionLabel: initial.versionLabel,
                }
              : undefined
          }
        />
        {/* Befund U5: dasselbe Steuerelement wie im Katalog-Reiter — tippbar
            UND anklickbar. Format und Parser stehen im Theme.
            Befund U6: Auch dieses Feld sitzt unter der Softwarestandstabelle
            und klappte nach oben darüber auf. */}
        <DateInput
          size="xs"
          label={t("maintenance.card.asOf")}
          placeholder={t("common.datePlaceholder")}
          value={asOf}
          onChange={setAsOf}
          maxDate={new Date()}
          popoverProps={DATE_POPOVER_DOWNWARDS}
          style={{ maxWidth: 200 }}
        />
        <Group gap="xs">
          <Button
            size="xs"
            disabled={picked === null || asOf === null}
            loading={upsertSoftware.isPending}
            onClick={() => {
              if (!picked || !asOf) return;
              upsertSoftware.mutate(
                {
                  systemId,
                  productId: picked.productId,
                  versionId: picked.versionId,
                  asOf: toLocalDateString(asOf),
                },
                { onSuccess: onDone },
              );
            }}
          >
            {t("common.save")}
          </Button>
          <Button size="xs" variant="subtle" onClick={onDone}>
            {t("common.cancel")}
          </Button>
        </Group>
        {upsertSoftware.isError && (
          <Text c="red" size="xs">
            {t("maintenance.card.saveError")}
          </Text>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Name eines Kundensystems, gespeichert beim Verlassen des Feldes.
 *
 * Bewusst kein eigener Speichern-Knopf: Die uebrigen Felder der Karte
 * (Stufe, Beauftragung) speichern ebenfalls sofort — ein einzelnes Feld mit
 * abweichender Bedienung waere die groessere Ueberraschung. Leer bleibt der
 * bisherige Name stehen, statt eine Zeile ohne Bezeichnung zu erzeugen.
 */
function SystemNameField({
  system,
  onSave,
  pending,
}: {
  system: { id: string; name: string };
  onSave: (name: string) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [wert, setWert] = useState(system.name);

  useEffect(() => setWert(system.name), [system.name, system.id]);

  return (
    <TextInput
      size="xs"
      label={t("maintenance.card.systemName")}
      value={wert}
      disabled={pending}
      onChange={(e) => setWert(e.currentTarget.value)}
      onBlur={() => {
        const sauber = wert.trim();
        if (sauber && sauber !== system.name) onSave(sauber);
        else setWert(system.name);
      }}
      style={{ width: 220 }}
    />
  );
}
